import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import Swal from 'sweetalert2';
import { LinkedIntakeToProgramme, LinkedNetsuiteProductCourseList, LinkedNetsuiteProductCourseListFilters } from '../../../services/ProgramService';
import { RenderCampus, RenderLearningMethod, RenderProgrammeType } from '../../../utils/CommonGroupingItem';
import PermissionsGate from '../../../utils/permissionGate';
import DataTableComponentBackendPagination from '../../common/DataTableComponentBackendPagination';
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import IconButton, { StyledActionGroup, StyledIconLink } from '../../library/IconButton';

/**
 * Picker table shown inside the "Link Programmes" modal: the course intakes for this
 * programme's Netsuite product that aren't linked yet, same table shell (search,
 * filters, export) as Career Library's list.
 * @param {Function} handlerefresh Refreshes the parent linked-programmes list after a link succeeds.
 * @param {boolean} toggle Bumped by setToggle to force this table to refetch.
 * @param {Function} setToggle Flips `toggle`.
 * @param {object} detail Programme detail record (used for its netsuite_product id).
 */
function LPTable({ handlerefresh, toggle, setToggle, detail }) {
    const { id } = useParams();

    const additionalParams = useMemo(
        () => ({
            programme_id: id,
            netsuite_product_id: detail?.netsuite_product,
            isAssigned: 0,
        }),
        [id, detail?.netsuite_product, toggle]
    );

    const getData = async (params, cancelToken) => {
        const { name, ...rest } = params;
        const res = await LinkedNetsuiteProductCourseList(
            { ...rest, search: name },
            cancelToken
        );
        return { data: { data_list: res.data?.data } };
    };

    const getFilters = () =>
        LinkedNetsuiteProductCourseListFilters(
            id,
            detail?.netsuite_product,
            0
        );

    const link = async (programmeId) => {
        try {
            await LinkedIntakeToProgramme({
                programme_id: programmeId,
                netsuite_id: detail?.netsuite_product,
                PMC_programme_id: id,
            });
            Swal.fire({
                icon: "success",
                title: "Programme Linked Successfully",
            });
            setToggle(!toggle);
            handlerefresh();
        } catch (err) {
            console.error("LPTable.js->link->Error", err);
        }
    };

    const columns = useMemo(() => [
        {
            name: "Name",
            selector: "name",
            sortField: "name",
            sortable: true,
            grow: 2,
            cell: (row) => (
                <span className="feature-name" title={row?.name ?? "-"}>
                    <span className="textLimit100">{row?.name ?? "-"}</span>
                </span>
            ),
        },
        {
            name: "Intake",
            selector: "intakes",
            sortField: "intakes",
            sortable: true,
            cell: (row) => (
                <span title={row?.intakes ?? "-"} className="textLimit100">
                    {row?.intakes ?? "-"}
                </span>
            ),
        },
        {
            name: "Campus",
            selector: "campus",
            sortField: "campus",
            sortable: true,
            cell: (row) => (
                <span title={row?.campus ?? "-"} className="textLimit100">
                    {row?.campus ? RenderCampus(row.campus).html : "-"}
                </span>
            ),
        },
        {
            name: "Delivery Method",
            selector: "type",
            sortField: "type",
            sortable: true,
            cell: (row) => (row?.type ? RenderLearningMethod(row.type).html : "-"),
        },
        {
            name: "Programme Type",
            selector: "subject_type",
            sortField: "subject_type",
            sortable: true,
            cell: (row) => (row?.subject_type ? RenderProgrammeType(row.subject_type).html : "-"),
        },
        {
            name: "Students",
            selector: "total_student",
            sortField: "total_student",
            sortable: true,
            cell: (row) => (
                <span title={row?.total_student ?? "-"}>
                    {row?.total_student != null ? row.total_student : "-"}
                </span>
            ),
        },
        {
            name: "Action",
            width: "112px",
            right: true,
            cell: (row) => (
                <StyledActionGroup>
                    <PermissionsGate
                        scopes={["matpedit"]}
                        RenderError={() => (
                            <IconButton
                                icon="folder-open"
                                variant="solid"
                                disabled
                                ariaLabel="Open programme"
                            />
                        )}
                    >
                        <StyledIconLink
                            to={`/courseAdministration/Programmes/programme/open/${row.id}/details`}
                            title="Open programme"
                        >
                            <IconButton
                                icon="folder-open"
                                variant="solid"
                                ariaLabel="Open programme"
                            />
                        </StyledIconLink>
                    </PermissionsGate>

                    <PermissionsGate
                        scopes={["matpadd"]}
                        RenderError={() => (
                            <IconButton
                                icon="link"
                                variant="solid"
                                disabled
                                ariaLabel="Link programme"
                            />
                        )}
                    >
                        <IconButton
                            icon="link"
                            variant="solid"
                            onClick={() => link(row.id)}
                            ariaLabel="Link programme"
                        />
                    </PermissionsGate>
                </StyledActionGroup>
            ),
        },
    ], [detail?.netsuite_product, id, toggle]);

    const exportRow = (row) => ({
        Name: row?.name || "-",
        Intake: row?.intakes || "-",
        Campus: row?.campus != null && row?.campus !== "" ? RenderCampus(row.campus).text : "-",
        "Delivery Method": row?.type ? RenderLearningMethod(row.type).text : "-",
        "Programme Type": row?.subject_type || "-",
        Students: row?.total_student != null ? row.total_student : "-",
    });

    return (
        <DataTableComponentBackendPagination
            columns={columns}
            getDataApi={getData}
            getFiltersApi={getFilters}
            tableButton={[]}
            fileName="Linkable_Programmes"
            mapExportRow={exportRow}
            filterConfig={[
                {
                    key: "intake",
                    label: "Intake",
                    optionsKey: "yearsAndIntaksFilters",
                    isOptionReversed: true,
                },
                {
                    key: "viaCampus",
                    label: "Campus",
                    optionsKey: "campusFilter",
                },
                {
                    key: "viaLearnType",
                    label: "Delivery Method",
                    optionsKey: "intakeTypeFilters",
                    renderFunction: RenderLearningMethod,
                },
                {
                    key: "viaCourseType",
                    label: "Programme Type",
                    optionsKey: "subjectTypeFilter",
                },
            ]}
            showExport={true}
            showFilter={true}
            showSearch={true}
            defaultSortField="name"
            defaultSortAsc={true}
            additionalParams={additionalParams}
            LoadingComponent={<SkeletonTicketList />}
        />
    );
}

export default LPTable;
